// src/context/DemoContext.jsx
import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import DemoOverlay from '../components/demo/DemoOverlay';

// action: 'info'  -> dim the page, explain the element, advance with the "Continue" button.
//         'click' -> highlight the real control, block everything else; the user MUST click
//                    it. That click performs its native action (e.g. navigation) and advances.
export const DEMO_STEPS = [
  {
    page: '/dashboard',
    action: 'info',
    selector: null,
    message:
      "Welcome to your home feed! This is where you view and interact with other users' posts — like, comment, share, or message them.",
  },
  {
    page: '/dashboard',
    action: 'info',
    selector: '[data-demo="post-card"]',
    message:
      'Each post looks like this. Open it to read more, like it, leave a comment, or message the creator.',
  },
  {
    page: '/dashboard',
    action: 'info',
    selector: '[data-demo="tag-filter"]',
    message:
      "You can sort your feed by tags to filter content by type, genre, format, and more.",
  },
  {
    page: '/dashboard',
    action: 'click',
    selector: '[data-demo="emerald-cta"]',
    message: 'Want more features? Click “Get Emerald” to explore our plans.',
  },
  {
    page: '/pricing',
    action: 'info',
    selector: null,
    message: 'These are our exclusive packages and what each one unlocks.',
  },
  {
    page: '/pricing',
    action: 'click',
    selector: '[data-demo="nav-discover"]',
    message: 'Looking for more people and posts? Click “Discover” in the navigation bar.',
  },
  {
    page: '/discover',
    action: 'info',
    selector: '[data-demo="search-bar"]',
    message: 'Search for people by name, or find posts by entering a title.',
  },
  {
    page: '/discover',
    action: 'click',
    selector: '[data-demo="nav-create"]',
    message: 'Want to share your own work? Click “Create”.',
  },
  {
    page: '/post',
    action: 'info',
    selector: '[data-demo="content-type"]',
    message: 'When creating a post, first choose the content type.',
  },
  {
    page: '/post',
    action: 'info',
    selector: '[data-demo="post-title"]',
    message: 'Add a title and description for your post.',
  },
  {
    page: '/post',
    action: 'info',
    selector: '[data-demo="post-tags"]',
    message: 'Choose relevant tags so the right people can find your work.',
  },
  {
    page: '/post',
    action: 'info',
    selector: '[data-demo="post-visibility"]',
    message: 'Control who can see your post — and whether to monetize it.',
  },
  {
    page: '/post',
    action: 'click',
    selector: '[data-demo="nav-feedback"]',
    message: 'Last stop — click “Feedback” to see our feedback system.',
  },
  {
    page: '/feedback-request',
    action: 'info',
    selector: '[data-demo="feedback-submit"]',
    message:
      'Writers: get structured human feedback within 24 hours for just $0.50 per page.',
  },
  {
    page: '/feedback-request',
    action: 'info',
    selector: '[data-demo="reader-section"]',
    message:
      "Readers: earn $0.25 per page by giving thoughtful feedback on scripts. That's the tour — enjoy Joint!",
  },
];

const DemoContext = createContext(null);

export function DemoProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  // Track the current path without retriggering the navigation effect on every route change
  // (route changes are driven BY the demo; we don't want them to fight it).
  const pathRef = useRef(location.pathname);
  useEffect(() => {
    pathRef.current = location.pathname;
  }, [location.pathname]);

  const startDemo = useCallback(() => {
    setCurrentStep(0);
    setIsActive(true);
  }, []);

  const endDemo = useCallback(() => {
    setIsActive(false);
    setCurrentStep(0);
  }, []);

  const advance = useCallback(() => {
    setCurrentStep((s) => Math.min(s + 1, DEMO_STEPS.length - 1));
  }, []);

  const goBack = useCallback(() => {
    setCurrentStep((s) => Math.max(s - 1, 0));
  }, []);

  // When the step changes, make sure we're on the right page.
  // 'click' steps are user-driven — their own click navigates — so we never auto-navigate
  // those, which avoids a race where we'd yank the user back mid-transition.
  useEffect(() => {
    if (!isActive) return;
    const step = DEMO_STEPS[currentStep];
    if (!step || step.action === 'click') return;
    if (pathRef.current !== step.page) navigate(step.page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, currentStep]);

  return (
    <DemoContext.Provider
      value={{
        isActive,
        currentStep,
        totalSteps: DEMO_STEPS.length,
        startDemo,
        endDemo,
        advance,
        goBack,
      }}
    >
      {children}
      {isActive && <DemoOverlay />}
    </DemoContext.Provider>
  );
}

export function useDemo() {
  const ctx = useContext(DemoContext);
  if (!ctx) throw new Error('useDemo must be used inside DemoProvider');
  return ctx;
}
